import { useEffect, useState, useMemo, useRef, memo } from 'react';
import { CircularProgress } from '@mui/material';
import { IframeWrapper, LoadingOverlay } from './styles';

const FORM_INIT_DELAY = 3000;
const GHL_FORM_DATA_KEY = 'nirva_ghl_form_data';

export const saveGHLFormData = (data) => {
  try {
    const existing = loadGHLFormData() || {};
    const merged = { ...existing, ...data };
    localStorage.setItem(GHL_FORM_DATA_KEY, JSON.stringify(merged));
  } catch (e) {}
};

export const loadGHLFormData = () => {
  try {
    const stored = localStorage.getItem(GHL_FORM_DATA_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

export const clearGHLFormData = () => {
  try {
    localStorage.removeItem(GHL_FORM_DATA_KEY);
  } catch (e) {}
};

const GHLForm = ({
  formURL = '',
  prefillData = {},
  onSubmitSuccess = () => {},
  isModal = false,
  ...rest
}) => {
  const savedData = loadGHLFormData() || {};
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);

  const iframeSrc = useMemo(() => {
    if (!formURL) return '';

    try {
      const url = new URL(formURL);
      const mergedData = { ...prefillData, ...savedData };

      Object.entries(mergedData).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
      });

      const finalUrl = url.href;

      if (iframeRef.current && iframeRef.current.src === finalUrl) {
        return iframeRef.current.src;
      }

      return finalUrl;
    } catch (e) {
      return formURL;
    }
    // eslint-disable-next-line
  }, [formURL, prefillData]);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, FORM_INIT_DELAY);
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      const { data } = event;
      if (data?.type === 'GHL_FORM_DATA_EXTRACT') {
        saveGHLFormData({
          phone: data.phone || '',
          ghl_phone_value: data.ghl_phone_value || '',
          ghl_dropdown_value: data.ghl_dropdown_value || '',
        });
        return;
      }

      if (event.origin.includes('leadconnectorhq.com')) {
        if (Array.isArray(data) && data[0] === 'set-sticky-contacts') {
          let contactData = null;
          try {
            const contactJson = data[2];
            contactData = typeof contactJson === 'string' ? JSON.parse(contactJson) : contactJson;
            const phone = contactData?.phone || contactData?.ghl_phone_value || '';
            if (phone) {
              saveGHLFormData({ phone, ghl_phone_value: phone });
            }
          } catch (e) {}
          onSubmitSuccess(contactData);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSubmitSuccess]);

  if (isLoading) {
    return (
      <IframeWrapper $isSubmitted={false} $isLoading={isLoading} {...rest}>
        <LoadingOverlay>
          <CircularProgress sx={{ color: '#492A18' }} />
        </LoadingOverlay>
      </IframeWrapper>
    );
  }

  return (
    <IframeWrapper $isSubmitted={false} $isLoading={isLoading} $isModal={isModal} {...rest}>
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Contact Form"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        id="contact-form-iframe"
      />
    </IframeWrapper>
  );
};

export default memo(GHLForm);
